import {
  Button,
  CloseButton,
  Flex,
  Heading,
  Modal,
  Pill,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { TermsContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { InputDate } from "../../InputDate";

const AcademicYearModal = ({ open, setOpen, academicYear, handleSave }) => {
  const { t } = useTranslation();
  const { insightsTerms, academicYears } = useContext(TermsContext);
  const showNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [includedInsightsTerms, setIncludedInsightsTerms] = useState([]);

  useEffect(() => {
    if (academicYear) {
      setName(academicYear.name);
      setStartDate(new Date(academicYear.start_at));
      setEndDate(new Date(academicYear.end_at));
    }
  }, [academicYear]);

  useEffect(() => {
    setIncludedInsightsTerms(
      insightsTerms
        .filter(
          (term) =>
            new Date(term.start_at).setHours(0, 0, 0, 0) >=
              startDate.setHours(0, 0, 0, 0) &&
            new Date(term.start_at).setHours(0, 0, 0, 0) <=
              endDate.setHours(0, 0, 0, 0)
        )
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
    );
  }, [startDate, endDate, insightsTerms]);

  const checkOverlap = () => {
    let overlap = "";
    const selectedYearStart = new Date(startDate).setHours(0, 0, 0, 0);
    const selectedYearEnd = new Date(endDate).setHours(0, 0, 0, 0);
    academicYears.forEach((t) => {
      if (academicYear?.id !== t.id) {
        const yearStart = new Date(t.start_at).setHours(0, 0, 0, 0);
        const yearEnd = new Date(t.end_at).setHours(0, 0, 0, 0);
        if (
          (selectedYearStart >= yearStart && selectedYearStart <= yearEnd) ||
          (selectedYearEnd >= yearStart && selectedYearEnd <= yearEnd) ||
          (selectedYearStart <= yearStart && selectedYearEnd >= yearEnd)
        ) {
          overlap = t;
        }
      }
    });
    return overlap;
  };

  const handleSaveAcademicYear = async () => {
    const overlapYear = checkOverlap();
    if (overlapYear) {
      showNotification(
        t("academicYears.overlapError", { year: overlapYear.name }),
        ERROR
      );
    } else if (startDate > endDate) {
      showNotification(t("insightsTerms.dateError"), ERROR);
    } else {
      setLoading(true);
      await handleSave(
        name,
        startDate,
        endDate,
        includedInsightsTerms.map((t) => t.id)
      );
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName(academicYear?.name || "");
    setStartDate(
      academicYear?.start_at ? new Date(academicYear.start_at) : new Date()
    );
    setEndDate(
      academicYear?.end_at ? new Date(academicYear.end_at) : new Date()
    );
    setLoading(false);
  };

  return (
    <Modal
      open={open}
      size="medium"
      label={
        academicYear ? t("academicYears.editYear") : t("academicYears.addYear")
      }
      onExited={handleClose}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading level="h2" as="h1">
              {academicYear
                ? t("academicYears.editYear")
                : t("academicYears.addYear")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={() => setOpen(false)}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" gap="small">
          <Flex.Item size="100%">
            <TextInput
              renderLabel={t("assessment.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Flex.Item>
          <Flex.Item>
            <InputDate
              id="date-1-selector"
              label={t("cyclesAndGoals.startDate")}
              selectHandler={(date) => setStartDate(date)}
              selectedDate={startDate}
            />
          </Flex.Item>
          <Flex.Item>
            <InputDate
              id="date-2-selector"
              label={t("cyclesAndGoals.endDate")}
              selectHandler={(date) => setEndDate(date)}
              selectedDate={endDate}
            />
          </Flex.Item>
          <Flex.Item size="100%">
            <Text lineHeight="double">
              <Text weight="bold">{t("academicYears.includedTerms")}: </Text>
              {includedInsightsTerms.length > 0
                ? includedInsightsTerms.map((term, key) => (
                    <Pill key={key} color="info" margin="0 small 0 0">
                      {term.name}
                    </Pill>
                  ))
                : t("global.none")}
            </Text>
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Flex wrap="wrap" gap="small">
          <Flex.Item>
            <Button onClick={() => setOpen(false)}>{t("global.cancel")}</Button>
          </Flex.Item>
          <Flex.Item>
            <Button
              type="submit"
              color="primary"
              onClick={handleSaveAcademicYear}
              interaction={
                !!name && includedInsightsTerms.length > 0 && !loading
                  ? "enabled"
                  : "disabled"
              }
            >
              {loading ? (
                <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
              ) : (
                t("global.save")
              )}
            </Button>
          </Flex.Item>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default AcademicYearModal;
